import { useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { validateString } from 'src/utils/string/stringUtil';
import {
	copyPreviousYearEQM3010_R01, deleteEQM3010_R01, downloadExcel_EQM3010_R01, getEQM3010_R01AllCount,
	getEQM3010_R01List, patchEQM3010_R01, postEQM3010_R01,
} from './Api_EQM3010_R01';
import { EQM3010_R01Dto } from './Dto_EQM3010_R01';
import { defaultEQM3010_R01Filter, EQM3010_R01Filter, useEQM3010_R01Store } from './Store_EQM3010_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM3010_R01Dto> | null>;
	setMessage: (message: string) => void;
}

/** 조회·저장 후 토스트/하단 메시지 표시 여부 */
type FetchMessageMode = 'show' | 'none';
interface FetchOptions { messageMode?: FetchMessageMode; }

/** 월별 검교정 계획 필드명 (1월~12월) */
const MONTH_FIELDS = [
	'month01', 'month02', 'month03', 'month04', 'month05', 'month06',
	'month07', 'month08', 'month09', 'month10', 'month11', 'month12',
] as const;

/**
 * 조회 직후 월 값 정규화
 * - API 빈문자열('') → 스위치 off 표시용 'N'으로 통일
 * - 원본 스냅샷·isModified 비교 기준을 맞춤
 */
const normalizeMonthValues = (rows: EQM3010_R01Dto[]): EQM3010_R01Dto[] =>
	rows.map((r) => {
		const fixed = { ...r };
		MONTH_FIELDS.forEach((f) => {
			// Y가 아니면 모두 N으로 통일 (스위치 컴포넌트 기준값)
			if (fixed[f] !== 'Y') fixed[f] = 'N';
		});
		return fixed;
	});

/**
 * 저장·삭제 API 전송 전 planYear·compCd 보정
 * - planYear: 조회 연도(filter.year) 기준
 * - compCd: 미입력 시 resolveCompCd 결과 사용
 */
const attachPlanMeta = (
	rows: EQM3010_R01Dto[],
	filter: EQM3010_R01Filter,
	resolvedCompCd: string,
): EQM3010_R01Dto[] =>
	rows.map((r) => ({
		...r,
		planYear: r.planYear || String(filter.year),
		compCd: r.compCd || resolvedCompCd,
	}));

/**
 * EQM3010 비즈니스 로직 (조회·등록·저장·삭제·전년복사)
 * Actionbar 이벤트 핸들러와 Table에 필요한 상태를 반환
 */
export const Content_EQM3010_R01 = ({ setMessage }: Props) => {
	const {
		EQM3010_R01Filter,
		EQM3010_R01PastFilter, setEQM3010_R01PastFilter,
		EQM3010_R01List, setEQM3010_R01List,
		deletedEQM3010_R01List, removeDeletedEQM3010_R01List,
		addDeletedEQM3010_R01List,
		selectedEQM3010_R01List, setSelectedEQM3010_R01List, removeSelectedEQM3010_R01List,
		setOriginalEQM3010_R01List,
		setAllCount, setScrollTop,
	} = useEQM3010_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	/** 사업장 드롭다운 목록 (미선택 시 첫 항목으로 API 파라미터 보정) */
	const { compCdList } = useCommonCodeStore();
	/** 전년복사 확인용 ConfirmModal 제어 */
	const { setConfirmModalVisible, setConfirmModalProps } = useUiuxStore();
	/** 그리드 행 클릭 선택 허용 여부 (순번 영역 클릭 직후 1회 차단용) */
	const [clickable, setClickable] = useState(true);

	useFetchActionbarAuth('EQM3010_R01');

	/**
	 * 사업장 코드 확정
	 * - 필터에 값이 없으면 compCdList 첫 번째 사업장 사용 (EQM2040 패턴)
	 */
	const resolveCompCd = useCallback((compCd: string) => {
		const defaultCompCd = compCdList[0]?.compCd ?? '';
		return compCd?.trim() ? compCd : defaultCompCd;
	}, [compCdList]);

	/** API 조회 파라미터 변환 (빈 문자열 필드는 undefined로 제외) */
	const getParams = useCallback((f: EQM3010_R01Filter): Partial<EQM3010_R01Filter> => ({
		compCd: resolveCompCd(f.compCd),
		year: f.year,
		equipmentCd: f.equipmentCd || undefined,
		equipmentNm: f.equipmentNm || undefined,
	}), [resolveCompCd]);

	/** 이전 조회 조건과 현재 조건이 동일한지 비교 */
	const isSameFilter = (a: EQM3010_R01Filter, b: EQM3010_R01Filter) =>
		a.compCd === b.compCd && a.year === b.year && a.equipmentCd === b.equipmentCd;

	/** 전체 건수 API 호출 후 스토어 갱신 */
	const renewalAllCount = useCallback(async () => {
		const res = await getEQM3010_R01AllCount(getParams(EQM3010_R01Filter));
		setAllCount(res.data.data);
		return res.data.data;
	}, [EQM3010_R01Filter, setAllCount, getParams]);

	/**
	 * 조회·재조회 공통 처리
	 * - search: Actionbar 조회 버튼
	 * - refetch: 저장·전년복사 후 목록 갱신 (messageMode: 'none'으로 토스트 생략 가능)
	 */
	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions,
	) => {
		/** 조회 완료 메시지 표시 여부 ('show' | 'none') */
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		/** 현재 검색조건과 이전 조회 조건 동일 여부 */
		const same = isSameFilter(EQM3010_R01Filter, EQM3010_R01PastFilter);

		// 조건 변경 또는 재조회 시 스크롤 위치 초기화
		if (!same || fetchType === 'refetch') setScrollTop(null);
		// 재조회·조건 변경 시 목록 비우고 로딩 깜빡임 방지
		if (fetchType === 'refetch' || !same) setEQM3010_R01List([]);

		const res = await getEQM3010_R01List('search', getParams(EQM3010_R01Filter));
		const newList = normalizeMonthValues([...res.data.data] as EQM3010_R01Dto[]);
		setEQM3010_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalEQM3010_R01List(newList);
		setSelectedEQM3010_R01List([]);
		removeDeletedEQM3010_R01List(deletedEQM3010_R01List);

		if (newList.length === 0) {
			// 조회 결과 없음
			if (messageMode === 'show') showWarning('조회된 계측기가 없습니다.');
		} else {
			setEQM3010_R01PastFilter(EQM3010_R01Filter);
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			}
		}

		setLoadingSpinner(false);
		if (messageMode === 'show') setTimeout(() => setMessage(''), 5000);
	}, [
		EQM3010_R01Filter, EQM3010_R01PastFilter, deletedEQM3010_R01List,
		setMessage, setLoadingSpinner, setOriginalEQM3010_R01List, setSelectedEQM3010_R01List,
		setEQM3010_R01List, removeDeletedEQM3010_R01List, renewalAllCount, setEQM3010_R01PastFilter, setScrollTop, getParams,
	]);

	/** Actionbar 조회 버튼 핸들러 */
	const fetchData = useCallback(async () => { await executeDataFetch('search'); }, [executeDataFetch]);

	/** Actionbar Excel 버튼 핸들러 */
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const response = await downloadExcel_EQM3010_R01(getParams(EQM3010_R01Filter));
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: '검교정 계획 등록.xlsx',
			});
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('검교정 계획 엑셀 다운로드 실패:', error);
			showWarning('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM3010_R01Filter, getParams, setLoadingSpinner]);

	/**
	 * Actionbar 등록 버튼 핸들러
	 * - 조회된 계측기 행은 유지하고 월 계획·비고만 초기화 후 inserted 표시
	 */
	const handleInsertClick = useCallback(() => {
		if (EQM3010_R01List.length === 0) {
			showWarning('먼저 조회해주세요.');
			return;
		}
		const cleared = EQM3010_R01List.map((row) => ({
			...row,
			month01: 'N', month02: 'N', month03: 'N', month04: 'N', month05: 'N', month06: 'N',
			month07: 'N', month08: 'N', month09: 'N', month10: 'N', month11: 'N', month12: 'N',
			remark: '', inserted: true, isModified: false,
		}));
		setEQM3010_R01List(cleared);
		removeSelectedEQM3010_R01List();
		setEQM3010_R01PastFilter(defaultEQM3010_R01Filter);
	}, [EQM3010_R01List, setEQM3010_R01List, removeSelectedEQM3010_R01List, setEQM3010_R01PastFilter]);

	/**
	 * Actionbar 저장 버튼 핸들러
	 * - inserted → POST, isModified → PATCH, 삭제표시 → DELETE
	 */
	const handleSaveClick = useCallback(async () => {
		const resolvedCompCd = resolveCompCd(EQM3010_R01Filter.compCd);
		/** 삭제 표시되지 않은 행만 대상 */
		const notDeleted = (r: EQM3010_R01Dto) =>
			!deletedEQM3010_R01List.includes(r.valueIndex.toString());

		const insertedList = attachPlanMeta(
			EQM3010_R01List.filter(
				(r) => notDeleted(r) && r.inserted && validateString(r.equipmentCd)),
			EQM3010_R01Filter,
			resolvedCompCd,
		);
		const modifiedList = attachPlanMeta(
			EQM3010_R01List.filter(
				(r) => notDeleted(r) && r.isModified && !r.inserted && validateString(r.equipmentCd)),
			EQM3010_R01Filter,
			resolvedCompCd,
		);

		if (insertedList.length === 0 && modifiedList.length === 0 && deletedEQM3010_R01List.length === 0) {
			showWarning('저장할 변경 내역이 없습니다.');
			return;
		}

		const deleteStringList = deletedEQM3010_R01List.map((i) => i.toString());
		const deleteTargetList = attachPlanMeta(
			EQM3010_R01List.filter((i) => deleteStringList.includes(i.valueIndex.toString())),
			EQM3010_R01Filter,
			resolvedCompCd,
		);

		const [insRes, modRes, delRes] = await Promise.all([
			insertedList.length > 0 ? postEQM3010_R01(insertedList) : Promise.resolve(undefined),
			modifiedList.length > 0 ? patchEQM3010_R01(modifiedList) : Promise.resolve(undefined),
			deletedEQM3010_R01List.length > 0
				? Promise.all(deleteTargetList.map((v) => deleteEQM3010_R01(v)))
				: Promise.resolve(undefined),
		]);

		if (insRes?.data.successFlag || modRes?.data.successFlag) {
			// 등록·수정 성공
			showSuccess('검교정 계획 저장이 완료되었습니다.');
			setMessage('검교정 계획 저장이 완료되었습니다.');
		} else if (delRes) {
			// 삭제만 수행된 경우
			showSuccess('검교정 계획 삭제가 완료되었습니다.');
			setMessage('검교정 계획 삭제가 완료되었습니다.');
		}

		setTimeout(() => setMessage(''), 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [EQM3010_R01List, EQM3010_R01Filter, deletedEQM3010_R01List, executeDataFetch, setMessage, resolveCompCd]);

	/**
	 * Actionbar 삭제 버튼 핸들러 (소프트 삭제)
	 * - 삭제 표시된 행이 있으면 → 표시 취소(되돌리기)
	 * - 선택 행만 있으면 → deletedEQM3010_R01List에 추가 (저장 시 API 삭제)
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedEQM3010_R01List.length > 0) {
			// 삭제 표시 전체 취소
			removeDeletedEQM3010_R01List(deletedEQM3010_R01List);
			removeSelectedEQM3010_R01List();
			setEQM3010_R01List([...EQM3010_R01List]);
		} else if (selectedEQM3010_R01List.length > 0) {
			// 선택 행 삭제 표시
			addDeletedEQM3010_R01List(selectedEQM3010_R01List);
			removeSelectedEQM3010_R01List(selectedEQM3010_R01List);
		}
	}, [
		selectedEQM3010_R01List, deletedEQM3010_R01List,
		addDeletedEQM3010_R01List, removeDeletedEQM3010_R01List,
		removeSelectedEQM3010_R01List, setEQM3010_R01List, EQM3010_R01List,
	]);

	/**
	 * 전년계획복사 확인 모달 (ConfirmModal)
	 * @returns 사용자가 복사를 선택하면 true, 취소하면 false
	 */
	const confirmCopyPreviousYear = useCallback(() => {
		return new Promise<boolean>((resolve) => {
			const closeModal = () => {
				setConfirmModalVisible(false);
				setConfirmModalProps(undefined);
			};

			setConfirmModalProps({
				title: '전년계획복사',
				message: '전년계획을 복사하면 등록된 계획은 모두 삭제됩니다.\n진행하시겠습니까?',
				confirmText: '복사',
				cancelText: '취소',
				onConfirm: () => { closeModal(); resolve(true); },
				onCancel: () => { closeModal(); resolve(false); },
			});
			setConfirmModalVisible(true);
		});
	}, [setConfirmModalProps, setConfirmModalVisible]);

	/**
	 * 전년계획복사 실행
	 * - 현재 연도 계획 전체 삭제 후 전년도 데이터를 복사 (백엔드 트랜잭션)
	 */
	const handleCopyPreviousYear = useCallback(async () => {
		const ok = await confirmCopyPreviousYear();
		if (!ok) return;

		setLoadingSpinner(true);
		await copyPreviousYearEQM3010_R01({
			compCd: resolveCompCd(EQM3010_R01Filter.compCd),
			planYear: String(EQM3010_R01Filter.year),
		});
		setLoadingSpinner(false);

		showSuccess('전년계획 복사가 완료되었습니다.');
		setMessage('전년계획 복사가 완료되었습니다.');
		setTimeout(() => setMessage(''), 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [EQM3010_R01Filter, executeDataFetch, setLoadingSpinner, setMessage, resolveCompCd, confirmCopyPreviousYear]);

	return {
		clickable, setClickable,
		fetchData,
		handleExcelClick,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
		handleCopyPreviousYear,
	};
};
